import { useState } from "react";

import { ExpandIcon, MinimizeIcon } from "../../Icons";
import { SkeletonLoader } from "../../SkeletonLoader";
import { Typo } from "../../Typo";
import { CreativePreview } from "../CreativePreview";
import { scaleDimensions } from "../CreativePreview/helpers";

import type { MarketingBannerRow } from "../types";
import type { MarketingBannerSize } from "./types";

type BannerPreviewPlaceholderProps = {
  banner?: MarketingBannerRow;
  size?: MarketingBannerSize;
  maxBoxSize?: number;
  maxBoxWidth?: number;
  showOriginalSize?: boolean;
  emptyLabel?: string;
  isLoading?: boolean;
};

const DISPLAY_AREA_HORIZONTAL_PADDING = 64;

export const BannerPreviewPlaceholder = ({
  banner,
  size,
  maxBoxSize = 320,
  maxBoxWidth,
  showOriginalSize = false,
  emptyLabel = "Banner preview",
  isLoading = false,
}: BannerPreviewPlaceholderProps) => {
  if (!size) {
    return (
      <Typo type="bodySM" textColor="text-text-secondary">
        {emptyLabel}
      </Typo>
    );
  }

  const boundedWidth =
    maxBoxWidth !== undefined
      ? Math.max(maxBoxWidth - DISPLAY_AREA_HORIZONTAL_PADDING, 1)
      : maxBoxSize;
  const scale = showOriginalSize ? 1 : Math.min(boundedWidth / size.width, 1);
  const width = Math.round(size.width * scale);
  const height = Math.round(size.height * scale);

  return (
    <div className="flex flex-col items-center">
      {isLoading ? (
        <SkeletonLoader width={width} height={height} variant="rounded" />
      ) : (
        banner && (
          <CreativePreview
            key={banner.id}
            banner={banner}
            maxWidth={width}
            maxHeight={height}
            frameless
          />
        )
      )}
    </div>
  );
};

const COLLAPSED_PREVIEW_CONTAINER_MIN_HEIGHT = 144;
const COLLAPSED_PREVIEW_MAX_WIDTH = 220;
const COLLAPSED_PREVIEW_MAX_HEIGHT = 96;

export const BannerPreviewPlaceholderMobile = ({
  banner,
  isLoading = false,
}: {
  banner: MarketingBannerRow;
  isLoading?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const original = { width: banner.width, height: banner.height };
  const skeletonSize = scaleDimensions(
    original,
    isExpanded
      ? original
      : {
          width: COLLAPSED_PREVIEW_MAX_WIDTH,
          height: COLLAPSED_PREVIEW_MAX_HEIGHT,
        },
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="relative w-full overflow-hidden rounded-lg bg-surface-secondary">
        <div
          data-testid="banner-preview-scroll-area"
          className={isExpanded ? "w-full overflow-x-auto" : "w-full"}
        >
          <div
            className="flex w-fit min-w-full items-center justify-center p-4"
            style={{
              minHeight: isExpanded
                ? undefined
                : COLLAPSED_PREVIEW_CONTAINER_MIN_HEIGHT,
            }}
          >
            {isLoading ? (
              <SkeletonLoader
                width={skeletonSize.width}
                height={skeletonSize.height}
                variant="rounded"
              />
            ) : isExpanded ? (
              <CreativePreview key={banner.id} banner={banner} frameless />
            ) : (
              <CreativePreview
                key={banner.id}
                banner={banner}
                maxWidth={COLLAPSED_PREVIEW_MAX_WIDTH}
                maxHeight={COLLAPSED_PREVIEW_MAX_HEIGHT}
                frameless
              />
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-label={
            isExpanded
              ? "button-collapse-banner-preview"
              : "button-expand-banner-preview"
          }
          className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-lg bg-surface-inverse-primary text-text-inverse-primary"
        >
          {isExpanded ? <MinimizeIcon /> : <ExpandIcon />}
        </button>
      </div>
    </div>
  );
};
